// C:\Users\aleja\Code\inventory_analytics\src\front\src\pages\OrmaCatalog.jsx
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Context } from "../store/appContext";
import "../styles/layout.css";
import "../styles/utilities.css";
import "../styles/forms.css";
import "../styles/catalog.css";

const currencyFormatter = new Intl.NumberFormat("es-ES", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const numberFormatter = new Intl.NumberFormat("es-ES", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const integerFormatter = new Intl.NumberFormat("es-ES", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const DEFAULT_PHYSICAL_FILTERS = {
  branch_name_exact: "07-LA PILA FOOD",
  warehouse_id: "MANTENIMIENTO",
  column: "",
  shelf: "",
  bin: "",
  second_location: "",
};

const DEFAULT_ADVANCED_FILTERS = {
  material_id: "",
  description: "",
  family: "",
  brand: "",
  model: "",
  manufacturer_ref: "",
  supplier_ref: "",
  alias: "",
  has_master_match: "",
};

const EMPTY_FACETS = {
  branches: [],
  warehouses: [],
  columns: [],
  shelves: [],
  bins: [],
  second_locations: [],
  families: [],
  brands: [],
};

const DEFAULT_PAGINATION = {
  page: 1,
  per_page: 25,
  total: 0,
  pages: 0,
};

const ORMA_VIEW_STORAGE_KEY = "inventoryAnalytics.ormaCatalog.view.v1";
const ORMA_SELECTION_STORAGE_KEY =
  "inventoryAnalytics.ormaCatalog.selectedParts.v1";

const readStoredJson = (storage, key, fallback) => {
  try {
    const rawValue = storage?.getItem(key);
    if (!rawValue) return fallback;

    const parsedValue = JSON.parse(rawValue);
    return parsedValue && typeof parsedValue === "object"
      ? parsedValue
      : fallback;
  } catch (error) {
    console.warn(`No se pudo leer ${key}:`, error);
    return fallback;
  }
};

const getInitialCatalogView = () => {
  if (typeof window === "undefined") {
    return {
      search: "",
      appliedSearch: "",
      filtersOpen: false,
      physicalFilters: DEFAULT_PHYSICAL_FILTERS,
      draftAdvancedFilters: DEFAULT_ADVANCED_FILTERS,
      appliedAdvancedFilters: DEFAULT_ADVANCED_FILTERS,
      page: 1,
      perPage: 25,
      sortBy: "material_id",
      sortOrder: "asc",
      selectedRowKey: "",
      scrollY: 0,
    };
  }

  const stored = readStoredJson(
    window.sessionStorage,
    ORMA_VIEW_STORAGE_KEY,
    {},
  );

  return {
    search: typeof stored.search === "string" ? stored.search : "",
    appliedSearch:
      typeof stored.appliedSearch === "string" ? stored.appliedSearch : "",
    filtersOpen: Boolean(stored.filtersOpen),
    physicalFilters: {
      ...DEFAULT_PHYSICAL_FILTERS,
      ...(stored.physicalFilters || {}),
    },
    draftAdvancedFilters: {
      ...DEFAULT_ADVANCED_FILTERS,
      ...(stored.draftAdvancedFilters || {}),
    },
    appliedAdvancedFilters: {
      ...DEFAULT_ADVANCED_FILTERS,
      ...(stored.appliedAdvancedFilters || {}),
    },
    page: Math.max(1, Number(stored.page || 1)),
    perPage: [25, 50, 100].includes(Number(stored.perPage))
      ? Number(stored.perPage)
      : 25,
    sortBy: [
      "material_id",
      "master_description",
      "family",
      "brand",
      "warehouse_id",
      "branch_name",
      "quantity",
      "cost",
    ].includes(stored.sortBy)
      ? stored.sortBy
      : "material_id",
    sortOrder: stored.sortOrder === "desc" ? "desc" : "asc",
    selectedRowKey:
      typeof stored.selectedRowKey === "string" ? stored.selectedRowKey : "",
    scrollY: Math.max(0, Number(stored.scrollY || 0)),
  };
};

const getInitialSelectedParts = () => {
  if (typeof window === "undefined") return [];

  const stored = readStoredJson(
    window.localStorage,
    ORMA_SELECTION_STORAGE_KEY,
    [],
  );

  if (!Array.isArray(stored)) return [];

  const seen = new Set();

  return stored
    .filter((item) => {
      const materialId = String(item?.material_id || "").trim();
      if (!materialId || seen.has(materialId)) return false;
      seen.add(materialId);
      return true;
    })
    .map((item) => ({
      material_id: String(item.material_id).trim(),
      description: String(item.description || "").trim(),
      brand: String(item.brand || "").trim(),
      location_summary: normalizeOperationalLocationSummary(
        item.location_summary,
      ),
      available_quantity: Number(item.available_quantity || 0),
      planned_quantity: Math.max(1, Number(item.planned_quantity || 1)),
      row_key: String(item.row_key || "").trim(),
      selected_at: item.selected_at || new Date().toISOString(),
    }));
};

const getOrmaRowKey = (row) =>
  String(
    row?.id ||
      `${row?.material_id || ""}-${row?.warehouse_id || ""}-${getLocationSummary(
        row,
      )}`,
  );

const INITIAL_CATALOG_VIEW = getInitialCatalogView();

const SORT_LABELS = {
  material_id: "ID material",
  master_description: "Descripción",
  family: "Familia",
  brand: "Marca",
  warehouse_id: "Almacén",
  branch_name: "Sucursal",
  quantity: "Cantidad",
  cost: "Coste",
};

const FILTER_LABELS = {
  q: "Búsqueda",
  branch_name_exact: "Sucursal",
  warehouse_id: "Almacén",
  column: "Columna",
  shelf: "Estante",
  bin: "Gabeta",
  second_location: "Segunda ubicación",
  material_id: "ID material",
  description: "Descripción",
  family: "Familia",
  brand: "Marca",
  model: "Modelo",
  manufacturer_ref: "Ref. fabricante",
  supplier_ref: "Ref. suministrador",
  alias: "Alias",
  has_master_match: "MAESTRO",
};

const PHYSICAL_ORDER = [
  "branch_name_exact",
  "warehouse_id",
  "column",
  "shelf",
  "bin",
  "second_location",
];

const PHYSICAL_OPTIONS = {
  branch_name_exact: "branches",
  warehouse_id: "warehouses",
  column: "columns",
  shelf: "shelves",
  bin: "bins",
  second_location: "second_locations",
};

const PHYSICAL_PLACEHOLDERS = {
  branch_name_exact: "Todas las sucursales",
  warehouse_id: "Todos los almacenes",
  column: "Todas las columnas",
  shelf: "Todos los estantes",
  bin: "Todas las gabetas",
  second_location: "Todas las segundas ubicaciones",
};

const formatCurrency = (value) => currencyFormatter.format(Number(value || 0));
const formatNumber = (value) => numberFormatter.format(Number(value || 0));
const formatInteger = (value) => integerFormatter.format(Number(value || 0));

const getErrorMessage = (result, fallback) =>
  result?.error || result?.msg || fallback;

const getDescription = (row) =>
  row?.master_description || row?.visor_description || "-";

const getLocationSummary = (row) =>
  [
    row?.branch_name || "",
    row?.warehouse_id ? `Alm. ${row.warehouse_id}` : "",
    row?.column_location ? `Col. ${row.column_location}` : "",
    row?.shelf ? `Est. ${row.shelf}` : "",
    row?.bin ? `Gab. ${row.bin}` : "",
    row?.second_location || "",
  ]
    .filter(Boolean)
    .join(" · ") || "-";

const getFilterValueLabel = (key, value) => {
  if (key === "has_master_match") {
    if (value === "true") return "Con MAESTRO";
    if (value === "false") return "Sin MAESTRO";
  }

  return value;
};


const normalizeOperationalLocationSummary = (value) =>
  String(value || "")
    .split(" · ")
    .filter((part) => /^(Col\.|Est\.|Gab\.)/.test(part.trim()))
    .join(" · ");

const getOperationalLocationSummary = (row) =>
  [
    row?.column_location ? `Col. ${row.column_location}` : "",
    row?.shelf ? `Est. ${row.shelf}` : "",
    row?.bin ? `Gab. ${row.bin}` : "",
  ]
    .filter(Boolean)
    .join(" · ") || "-";

const getSortIndicator = (sortBy, sortOrder, key) => {
  if (sortBy !== key) return "";
  return sortOrder === "asc" ? " ▲" : " ▼";
};

const pruneEmptyParams = (params) =>
  Object.entries(params).reduce((result, [key, value]) => {
    if (value !== null && value !== undefined && String(value).trim() !== "") {
      result[key] = value;
    }
    return result;
  }, {});

const toFacetParams = (physicalFilters, advancedFilters = {}) =>
  pruneEmptyParams({
    branch_name: physicalFilters.branch_name_exact,
    warehouse_id: physicalFilters.warehouse_id,
    column: physicalFilters.column,
    shelf: physicalFilters.shelf,
    bin: physicalFilters.bin,
    family: advancedFilters.family,
    brand: advancedFilters.brand,
    has_master_match: advancedFilters.has_master_match,
  });

const FieldValue = ({ label, value }) => (
  <div className="orma-detail-field">
    <span>{label}</span>
    <strong title={value || ""}>{value || "-"}</strong>
  </div>
);

const LocationValue = ({ label, value }) =>
  value ? (
    <span className="orma-location-pill">
      <span>{label}</span>
      <strong>{value}</strong>
    </span>
  ) : null;

const OrmaCatalog = () => {
  const { actions } = useContext(Context);
  const [search, setSearch] = useState(INITIAL_CATALOG_VIEW.search);
  const [appliedSearch, setAppliedSearch] = useState(
    INITIAL_CATALOG_VIEW.appliedSearch,
  );
  const [filtersOpen, setFiltersOpen] = useState(
    INITIAL_CATALOG_VIEW.filtersOpen,
  );
  const [physicalFilters, setPhysicalFilters] = useState(
    INITIAL_CATALOG_VIEW.physicalFilters,
  );
  const [draftAdvancedFilters, setDraftAdvancedFilters] = useState(
    INITIAL_CATALOG_VIEW.draftAdvancedFilters,
  );
  const [appliedAdvancedFilters, setAppliedAdvancedFilters] = useState(
    INITIAL_CATALOG_VIEW.appliedAdvancedFilters,
  );
  const [facets, setFacets] = useState(EMPTY_FACETS);
  const [facetsLoading, setFacetsLoading] = useState(false);
  const [facetsError, setFacetsError] = useState("");
  const [page, setPage] = useState(INITIAL_CATALOG_VIEW.page);
  const [perPage, setPerPage] = useState(INITIAL_CATALOG_VIEW.perPage);
  const [sortBy, setSortBy] = useState(INITIAL_CATALOG_VIEW.sortBy);
  const [sortOrder, setSortOrder] = useState(
    INITIAL_CATALOG_VIEW.sortOrder,
  );
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [selectedRowKey, setSelectedRowKey] = useState(
    INITIAL_CATALOG_VIEW.selectedRowKey,
  );
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [selectedParts, setSelectedParts] = useState(
    getInitialSelectedParts,
  );
  const [selectionModalOpen, setSelectionModalOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingSelected, setExportingSelected] = useState(false);
  const detailCloseButtonRef = useRef(null);
  const selectionCloseButtonRef = useRef(null);
  const selectionTriggerRef = useRef(null);
  const detailRequestControllerRef = useRef(null);
  const lastFocusedElementRef = useRef(null);
  const rowElementsRef = useRef(new Map());
  const scrollPositionRef = useRef(INITIAL_CATALOG_VIEW.scrollY);
  const initialScrollRestoredRef = useRef(false);

  useEffect(() => {
    const controller = new AbortController();

    const loadFacets = async () => {
      setFacetsLoading(true);
      setFacetsError("");

      const result = await actions.getOrmaCatalogFacets(
        toFacetParams(physicalFilters, appliedAdvancedFilters),
        { signal: controller.signal },
      );

      if (result?.aborted) return;

      if (!result.success) {
        setFacets(EMPTY_FACETS);
        setFacetsError(
          getErrorMessage(
            result,
            "No se pudieron cargar las opciones de filtro",
          ),
        );
        setFacetsLoading(false);
        return;
      }

      setFacets({
        ...EMPTY_FACETS,
        ...(result.data || {}),
      });
      setFacetsLoading(false);
    };

    loadFacets();

    return () => controller.abort();
  }, [actions, appliedAdvancedFilters, physicalFilters]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      const normalizedSearch = search.trim();
      if (normalizedSearch !== appliedSearch) {
        setAppliedSearch(normalizedSearch);
        setPage(1);
      }
    }, 400);

    return () => window.clearTimeout(timeout);
  }, [search, appliedSearch]);

  useEffect(() => {
    const controller = new AbortController();

    const loadSearch = async () => {
      const params = pruneEmptyParams({
        q: appliedSearch,
        page,
        per_page: perPage,
        sort_by: sortBy,
        sort_order: sortOrder,
        ...physicalFilters,
        ...appliedAdvancedFilters,
      });

      setLoading(true);
      setError("");

      const result = await actions.searchOrmaCatalog(params, {
        signal: controller.signal,
      });

      if (result?.aborted) return;

      if (!result.success) {
        setRows([]);
        setPagination(DEFAULT_PAGINATION);
        setError(
          result.status === 404
            ? "No existe una carga ORMA usable para consultar."
            : getErrorMessage(result, "No se pudo buscar en el catálogo ORMA"),
        );
        setLoading(false);
        return;
      }

      setRows(Array.isArray(result.data?.items) ? result.data.items : []);
      setPagination({
        ...DEFAULT_PAGINATION,
        ...(result.data?.pagination || {}),
      });
      setLoading(false);
    };

    loadSearch();

    return () => controller.abort();
  }, [
    actions,
    appliedAdvancedFilters,
    appliedSearch,
    page,
    perPage,
    physicalFilters,
    sortBy,
    sortOrder,
  ]);

  useEffect(
    () => () => {
      detailRequestControllerRef.current?.abort();
    },
    [],
  );

  useEffect(() => {
    const handleScroll = () => {
      scrollPositionRef.current = window.scrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const viewState = {
      search,
      appliedSearch,
      filtersOpen,
      physicalFilters,
      draftAdvancedFilters,
      appliedAdvancedFilters,
      page,
      perPage,
      sortBy,
      sortOrder,
      selectedRowKey,
      scrollY: scrollPositionRef.current,
    };

    try {
      window.sessionStorage.setItem(
        ORMA_VIEW_STORAGE_KEY,
        JSON.stringify(viewState),
      );
    } catch (storageError) {
      console.warn(
        "No se pudo guardar la vista del catálogo ORMA:",
        storageError,
      );
    }
  }, [
    appliedAdvancedFilters,
    appliedSearch,
    draftAdvancedFilters,
    filtersOpen,
    page,
    perPage,
    physicalFilters,
    search,
    selectedRowKey,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        ORMA_SELECTION_STORAGE_KEY,
        JSON.stringify(selectedParts),
      );
    } catch (storageError) {
      console.warn(
        "No se pudieron guardar los repuestos seleccionados:",
        storageError,
      );
    }
  }, [selectedParts]);

  useEffect(() => {
    if (
      loading ||
      rows.length === 0 ||
      initialScrollRestoredRef.current
    ) {
      return;
    }

    initialScrollRestoredRef.current = true;

    window.requestAnimationFrame(() => {
      const selectedRow = selectedRowKey
        ? rowElementsRef.current.get(selectedRowKey)
        : null;

      if (selectedRow) {
        selectedRow.scrollIntoView({
          block: "center",
          inline: "nearest",
          behavior: "auto",
        });
        return;
      }

      window.scrollTo({
        top: scrollPositionRef.current,
        behavior: "auto",
      });
    });
  }, [loading, rows, selectedRowKey]);

  useEffect(() => {
    if (!selectionModalOpen) return undefined;

    selectionCloseButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeSelectionModal();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectionModalOpen]);

  useEffect(() => {
    if (!selectedMaterial) return undefined;

    detailCloseButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeDetail();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedMaterial]);

  const totalRows = Number(pagination.total || 0);
  const totalPages = Number(pagination.pages || 0);
  const firstRowIndex =
    totalRows > 0 ? (Number(pagination.page || page) - 1) * perPage + 1 : 0;
  const lastRowIndex = Math.min(firstRowIndex + rows.length - 1, totalRows);
  const material = detail?.material || null;
  const hasDetailResponse = detail !== null;
  const locations = Array.isArray(detail?.locations) ? detail.locations : [];
  const summary = detail?.summary || {};
  const inconsistencies = detail?.inconsistencies || null;
  const hasInconsistencies =
    inconsistencies &&
    Object.values(inconsistencies).some(
      (values) => Array.isArray(values) && values.length > 0,
    );

  const selectedPartIds = useMemo(
    () =>
      new Set(
        selectedParts.map((item) =>
          String(item.material_id || "").trim(),
        ),
      ),
    [selectedParts],
  );

  const activeFilterChips = useMemo(() => {
    const chips = [];

    if (appliedSearch) {
      chips.push({
        key: "q",
        label: FILTER_LABELS.q,
        value: appliedSearch,
        removable: true,
      });
    }

    PHYSICAL_ORDER.forEach((key) => {
      const value = physicalFilters[key];
      if (String(value || "").trim()) {
        chips.push({
          key,
          label: FILTER_LABELS[key],
          value: getFilterValueLabel(key, value),
          removable: true,
        });
      }
    });

    Object.entries(appliedAdvancedFilters).forEach(([key, value]) => {
      if (String(value || "").trim()) {
        chips.push({
          key,
          label: FILTER_LABELS[key] || key,
          value: getFilterValueLabel(key, value),
          removable: true,
        });
      }
    });

    return chips;
  }, [appliedAdvancedFilters, appliedSearch, physicalFilters]);

  const activeFilterCount = activeFilterChips.length;


  const handleExport = async () => {
    if (exporting || loading || totalRows <= 0) return;

    const params = pruneEmptyParams({
      q: appliedSearch,
      sort_by: sortBy,
      sort_order: sortOrder,
      ...physicalFilters,
      ...appliedAdvancedFilters,
    });

    setExporting(true);
    setError("");

    const result = await actions.exportOrmaCatalog(params);

    if (!result.success) {
      const message = getErrorMessage(
        result,
        "No se pudo exportar el catálogo ORMA",
      );
      setError(message);
      actions.showToast?.(message, "error");
      setExporting(false);
      return;
    }

    actions.showToast?.(
      `Excel generado con ${formatInteger(totalRows)} filas filtradas`,
      "success",
    );
    setExporting(false);
  };


  const handleExportSelected = async () => {
    if (exportingSelected || selectedParts.length === 0) return;

    setExportingSelected(true);
    const result = await actions.exportSelectedOrmaCatalog(
      selectedParts.map((item) => ({
        material_id: item.material_id,
        planned_quantity: item.planned_quantity,
        description: item.description,
        brand: item.brand,
        location_summary: item.location_summary,
      })),
    );

    if (!result.success) {
      const message = getErrorMessage(
        result,
        "No se pudieron exportar los elementos guardados",
      );
      actions.showToast?.(message, "error");
      setExportingSelected(false);
      return;
    }

    actions.showToast?.(
      `Excel generado con ${formatInteger(selectedParts.length)} elementos guardados`,
      "success",
    );
    setExportingSelected(false);
  };

  const handlePhysicalChange = (key, value) => {
    setPhysicalFilters((current) => {
      const next = { ...current, [key]: value };
      const changedIndex = PHYSICAL_ORDER.indexOf(key);

      PHYSICAL_ORDER.forEach((lowerKey, index) => {
        if (index > changedIndex) {
          next[lowerKey] = "";
        }
      });

      return next;
    });
    setPage(1);
  };

  const handleDraftAdvancedFilterChange = (key, value) => {
    setDraftAdvancedFilters((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const handleApplyFilters = (event) => {
    event.preventDefault();
    setAppliedAdvancedFilters({ ...draftAdvancedFilters });
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearch("");
    setAppliedSearch("");
    setPhysicalFilters(DEFAULT_PHYSICAL_FILTERS);
    setDraftAdvancedFilters(DEFAULT_ADVANCED_FILTERS);
    setAppliedAdvancedFilters(DEFAULT_ADVANCED_FILTERS);
    setPage(1);
  };

  const handleRemoveFilter = (key) => {
    if (key === "q") {
      setSearch("");
      setAppliedSearch("");
      setPage(1);
      return;
    }

    if (PHYSICAL_ORDER.includes(key)) {
      handlePhysicalChange(key, "");
      return;
    }

    setDraftAdvancedFilters((current) => ({
      ...current,
      [key]: "",
    }));
    setAppliedAdvancedFilters((current) => ({
      ...current,
      [key]: "",
    }));
    setPage(1);
  };

  const renderLocationCell = (row) => {
    const summaryText = getOperationalLocationSummary(row);

    if (summaryText === "-") return "-";

    return (
      <div className="orma-location-stack" title={summaryText}>
        <LocationValue label="Col." value={row.column_location} />
        <LocationValue label="Est." value={row.shelf} />
        <LocationValue label="Gab." value={row.bin} />
      </div>
    );
  };

  const handleSort = (key) => {
    setSortBy(key);
    setSortOrder((current) =>
      sortBy === key && current === "asc" ? "desc" : "asc",
    );
    setPage(1);
  };

  const renderSortableHeader = (key, label, className = "") => (
    <th className={className}>
      <button
        type="button"
        className="btn btn-link btn-sm p-0 text-decoration-none text-reset fw-semibold"
        onClick={() => handleSort(key)}
        disabled={loading}
        title={`Ordenar por ${label}`}
      >
        {label}
        {getSortIndicator(sortBy, sortOrder, key)}
      </button>
    </th>
  );

  const loadDetail = async (
    materialId,
    triggerElement,
    rowKey = "",
  ) => {
    detailRequestControllerRef.current?.abort();
    const controller = new AbortController();
    detailRequestControllerRef.current = controller;
    lastFocusedElementRef.current = triggerElement || document.activeElement;
    scrollPositionRef.current = window.scrollY;

    if (rowKey) {
      setSelectedRowKey(rowKey);
    }

    setSelectedMaterial(materialId);
    setDetail(null);
    setDetailLoading(true);
    setDetailError("");

    const result = await actions.getOrmaCatalogMaterial(materialId, {
      signal: controller.signal,
    });

    if (result?.aborted || controller.signal.aborted) return;

    if (detailRequestControllerRef.current !== controller) return;

    if (!result.success) {
      setDetailError(
        getErrorMessage(result, "No se pudo cargar el material ORMA"),
      );
      setDetailLoading(false);
      if (detailRequestControllerRef.current === controller) {
        detailRequestControllerRef.current = null;
      }
      return;
    }

    setDetail(result.data || {});
    setDetailLoading(false);
    if (detailRequestControllerRef.current === controller) {
      detailRequestControllerRef.current = null;
    }
  };

  const closeDetail = () => {
    detailRequestControllerRef.current?.abort();
    detailRequestControllerRef.current = null;
    setSelectedMaterial(null);
    setDetail(null);
    setDetailLoading(false);
    setDetailError("");

    window.setTimeout(() => {
      const selectedRow = selectedRowKey
        ? rowElementsRef.current.get(selectedRowKey)
        : null;
      const focusTarget = selectedRow || lastFocusedElementRef.current;

      focusTarget?.scrollIntoView?.({
        block: "center",
        inline: "nearest",
        behavior: "auto",
      });
      focusTarget?.focus?.();
      lastFocusedElementRef.current = null;
    }, 0);
  };

  const openSelectionModal = (triggerElement) => {
    selectionTriggerRef.current = triggerElement || document.activeElement;
    setSelectionModalOpen(true);
  };

  const closeSelectionModal = () => {
    setSelectionModalOpen(false);
    window.setTimeout(() => {
      selectionTriggerRef.current?.focus?.();
      selectionTriggerRef.current = null;
    }, 0);
  };

  const addSelectedPart = (row) => {
    const materialId = String(row?.material_id || "").trim();
    if (!materialId) return;

    const rowKey = getOrmaRowKey(row);

    setSelectedParts((current) => {
      const existing = current.find(
        (item) => item.material_id === materialId,
      );

      const nextItem = {
        material_id: materialId,
        description: getDescription(row),
        brand: String(row?.brand || "").trim(),
        location_summary: getOperationalLocationSummary(row),
        available_quantity: Number(row?.quantity || 0),
        planned_quantity: existing?.planned_quantity || 1,
        row_key: rowKey,
        selected_at: existing?.selected_at || new Date().toISOString(),
      };

      if (existing) {
        return current.map((item) =>
          item.material_id === materialId ? nextItem : item,
        );
      }

      return [...current, nextItem];
    });
  };

  const removeSelectedPart = (materialId) => {
    const normalizedMaterialId = String(materialId || "").trim();
    setSelectedParts((current) =>
      current.filter(
        (item) => item.material_id !== normalizedMaterialId,
      ),
    );
  };

  const toggleSelectedPart = (row) => {
    const materialId = String(row?.material_id || "").trim();
    if (!materialId) return;

    if (selectedPartIds.has(materialId)) {
      removeSelectedPart(materialId);
      return;
    }

    addSelectedPart(row);
  };

  const updateSelectedPartQuantity = (materialId, value) => {
    const normalizedValue = Math.max(1, Number(value || 1));

    setSelectedParts((current) =>
      current.map((item) =>
        item.material_id === materialId
          ? {
              ...item,
              planned_quantity: normalizedValue,
            }
          : item,
      ),
    );
  };

  const clearSelectedParts = () => {
    if (
      selectedParts.length > 0 &&
      !window.confirm("¿Vaciar todos los repuestos seleccionados?")
    ) {
      return;
    }

    setSelectedParts([]);
  };

  const openSelectedPartDetail = (item, triggerElement) => {
    setSelectionModalOpen(false);
    selectionTriggerRef.current = null;

    window.setTimeout(() => {
      const rowElement = item.row_key
        ? rowElementsRef.current.get(item.row_key)
        : null;

      loadDetail(
        item.material_id,
        rowElement || triggerElement,
        item.row_key || "",
      );
    }, 0);
  };

  const isPhysicalDisabled = (key) => {
    if (key === "branch_name_exact")
      return facetsLoading || facets.branches.length === 0;
    const previousKey = PHYSICAL_ORDER[PHYSICAL_ORDER.indexOf(key) - 1];
    const optionKey = PHYSICAL_OPTIONS[key];

    return (
      facetsLoading ||
      !physicalFilters[previousKey] ||
      !Array.isArray(facets[optionKey]) ||
      facets[optionKey].length === 0
    );
  };

  const renderPhysicalSelect = (key, label) => {
    const optionKey = PHYSICAL_OPTIONS[key];
    const options = Array.isArray(facets[optionKey]) ? facets[optionKey] : [];

    return (
      <div className="catalog-filter-group">
        <label htmlFor={`orma_scope_${key}`}>{label}</label>
        <select
          id={`orma_scope_${key}`}
          className="form-select"
          value={physicalFilters[key]}
          onChange={(event) => handlePhysicalChange(key, event.target.value)}
          disabled={isPhysicalDisabled(key)}
        >
          <option value="">{PHYSICAL_PLACEHOLDERS[key]}</option>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
    );
  };

  return (
    <div className="container mt-5 catalog-page orma-catalog-page">
      <div className="page-header catalog-header">
        <div className="catalog-title-block">
          <span className="catalog-title-icon" aria-hidden="true">
            <i className="bi bi-search" />
          </span>
          <div>
            <h1 className="page-title">Catálogo ORMA</h1>
            <p className="page-subtitle">
              Busca materiales, filtra por ubicación física y consulta stock
              operativo.
            </p>
          </div>
        </div>

        <div className="orma-catalog-header-actions">
          <button
            type="button"
            className="btn btn-secondary orma-export-button"
            onClick={handleExport}
            disabled={exporting || loading || totalRows <= 0}
          >
            <i className="bi bi-file-earmark-excel" aria-hidden="true" />
            {exporting
              ? "Generando Excel..."
              : `Exportar Excel (${formatInteger(totalRows)})`}
          </button>

          <button
            type="button"
            className="btn btn-primary orma-selection-trigger"
            onClick={(event) => openSelectionModal(event.currentTarget)}
          >
            <i className="bi bi-bookmark-check" aria-hidden="true" />
            Repuestos seleccionados
            <span className="orma-selection-count">
              {formatInteger(selectedParts.length)}
            </span>
          </button>
        </div>
      </div>

      <section className="catalog-card orma-scope-card">
        <div className="catalog-section-head">
          <div>
            <h2>Ámbito físico</h2>
            <p>
              Sucursal → Almacén → Columna → Estante → Gabeta → Segunda
              ubicación.
            </p>
          </div>
          {facetsLoading && (
            <span className="catalog-table-count">Cargando filtros...</span>
          )}
        </div>

        {facetsError && (
          <div
            className="catalog-state catalog-state-error catalog-state-inline"
            aria-live="polite"
          >
            {facetsError}
          </div>
        )}

        <div className="orma-physical-grid" aria-busy={facetsLoading}>
          {renderPhysicalSelect("branch_name_exact", "Sucursal")}
          {renderPhysicalSelect("warehouse_id", "Almacén")}
          {renderPhysicalSelect("column", "Columna")}
          {renderPhysicalSelect("shelf", "Estante")}
          {renderPhysicalSelect("bin", "Gabeta")}
          {renderPhysicalSelect("second_location", "Segunda ubicación")}
        </div>
      </section>

      <section className="catalog-card orma-search-card">
        <div className="orma-filter-toggle-row">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setFiltersOpen((current) => !current)}
            aria-expanded={filtersOpen}
            aria-controls="orma_advanced_filters"
          >
            Afinar búsqueda{activeFilterCount ? ` (${activeFilterCount})` : ""}
          </button>
        </div>

        {filtersOpen && (
          <form
            id="orma_advanced_filters"
            className="orma-advanced-filters"
            onSubmit={handleApplyFilters}
          >
            <div className="catalog-filter-group">
              <label htmlFor="orma_filter_material_id">ID material</label>
              <input
                id="orma_filter_material_id"
                type="text"
                value={draftAdvancedFilters.material_id}
                onChange={(event) =>
                  handleDraftAdvancedFilterChange(
                    "material_id",
                    event.target.value,
                  )
                }
              />
            </div>
            <div className="catalog-filter-group">
              <label htmlFor="orma_filter_description">Descripción</label>
              <input
                id="orma_filter_description"
                type="text"
                value={draftAdvancedFilters.description}
                onChange={(event) =>
                  handleDraftAdvancedFilterChange(
                    "description",
                    event.target.value,
                  )
                }
              />
            </div>
            <div className="catalog-filter-group">
              <label htmlFor="orma_filter_family">Familia</label>
              <select
                id="orma_filter_family"
                className="form-select"
                value={draftAdvancedFilters.family}
                onChange={(event) =>
                  handleDraftAdvancedFilterChange("family", event.target.value)
                }
              >
                <option value="">Todas las familias</option>
                {facets.families.map((family) => (
                  <option key={family} value={family}>
                    {family}
                  </option>
                ))}
              </select>
            </div>
            <div className="catalog-filter-group">
              <label htmlFor="orma_filter_brand">Marca</label>
              <input
                id="orma_filter_brand"
                type="text"
                list="orma_brand_suggestions"
                value={draftAdvancedFilters.brand}
                onChange={(event) =>
                  handleDraftAdvancedFilterChange("brand", event.target.value)
                }
              />
              <datalist id="orma_brand_suggestions">
                {facets.brands.map((brand) => (
                  <option key={brand} value={brand} />
                ))}
              </datalist>
            </div>
            <div className="catalog-filter-group">
              <label htmlFor="orma_filter_model">Modelo</label>
              <input
                id="orma_filter_model"
                type="text"
                value={draftAdvancedFilters.model}
                onChange={(event) =>
                  handleDraftAdvancedFilterChange("model", event.target.value)
                }
              />
            </div>
            <div className="catalog-filter-group">
              <label htmlFor="orma_filter_manufacturer_ref">
                Referencia fabricante
              </label>
              <input
                id="orma_filter_manufacturer_ref"
                type="text"
                value={draftAdvancedFilters.manufacturer_ref}
                onChange={(event) =>
                  handleDraftAdvancedFilterChange(
                    "manufacturer_ref",
                    event.target.value,
                  )
                }
              />
            </div>
            <div className="catalog-filter-group">
              <label htmlFor="orma_filter_supplier_ref">
                Referencia suministrador
              </label>
              <input
                id="orma_filter_supplier_ref"
                type="text"
                value={draftAdvancedFilters.supplier_ref}
                onChange={(event) =>
                  handleDraftAdvancedFilterChange(
                    "supplier_ref",
                    event.target.value,
                  )
                }
              />
            </div>
            <div className="catalog-filter-group">
              <label htmlFor="orma_filter_alias">Alias</label>
              <input
                id="orma_filter_alias"
                type="text"
                value={draftAdvancedFilters.alias}
                onChange={(event) =>
                  handleDraftAdvancedFilterChange("alias", event.target.value)
                }
              />
            </div>
            <div className="catalog-filter-group">
              <label htmlFor="orma_filter_master">Coincidencia MAESTRO</label>
              <select
                id="orma_filter_master"
                className="form-select"
                value={draftAdvancedFilters.has_master_match}
                onChange={(event) =>
                  handleDraftAdvancedFilterChange(
                    "has_master_match",
                    event.target.value,
                  )
                }
              >
                <option value="">Todos</option>
                <option value="true">Con MAESTRO</option>
                <option value="false">Sin MAESTRO</option>
              </select>
            </div>
            <div className="orma-filter-actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                Aplicar filtros
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleClearFilters}
                disabled={loading}
              >
                Limpiar filtros
              </button>
            </div>
          </form>
        )}
      </section>

      {activeFilterChips.length > 0 && (
        <section
          className="catalog-card orma-active-filter-card"
          aria-label="Filtros activos"
        >
          <div className="orma-active-filters">
            {activeFilterChips.map((filter) => (
              <span key={filter.key} className="orma-active-filter-chip">
                {filter.label}: <strong>{filter.value}</strong>
                <button
                  type="button"
                  onClick={() => handleRemoveFilter(filter.key)}
                  aria-label={`Eliminar filtro ${filter.label}`}
                  disabled={loading}
                >
                  <i className="bi bi-x" aria-hidden="true" />
                </button>
              </span>
            ))}
          </div>
        </section>
      )}

      <section className="catalog-card orma-search-card orma-search-card-primary">
        <div className="orma-search-main">
          <label htmlFor="orma_catalog_search">
            Buscar en todo el catálogo
          </label>
          <div className="catalog-search-input-wrap orma-search-input-wrap">
            <span aria-hidden="true">
              <i className="bi bi-search" />
            </span>
            <input
              id="orma_catalog_search"
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="ID material, descripción, referencia, marca, familia, modelo o alias"
              aria-describedby="orma_search_hint"
            />
            {search && (
              <button
                type="button"
                className="orma-clear-search"
                onClick={() => setSearch("")}
                aria-label="Limpiar búsqueda global"
                disabled={loading}
              >
                <i className="bi bi-x" aria-hidden="true" />
              </button>
            )}
          </div>
          <p id="orma_search_hint" className="orma-search-hint">
            La búsqueda global usa el parámetro q y se aplica automáticamente.
          </p>
        </div>
      </section>

      <section className="catalog-card">
        <div className="catalog-section-head">
          <div>
            <h2>Resultados</h2>
            <p>
              Ordenación y paginación aplicadas en backend. Orden actual:{" "}
              {SORT_LABELS[sortBy]} {sortOrder}.
            </p>
          </div>
          <span className="catalog-table-count" aria-live="polite">
            {totalRows > 0
              ? `${formatInteger(firstRowIndex)}-${formatInteger(lastRowIndex)} de ${formatInteger(totalRows)}`
              : `0 de ${formatInteger(totalRows)}`}
          </span>
        </div>

        {loading && (
          <div
            className="catalog-state catalog-state-loading catalog-state-inline"
            aria-live="polite"
          >
            Buscando en catálogo ORMA...
          </div>
        )}

        {!loading && error && (
          <div
            className="catalog-state catalog-state-error catalog-state-inline"
            aria-live="assertive"
          >
            {error}
          </div>
        )}

        {!loading && !error && rows.length === 0 && (
          <div
            className="catalog-state catalog-state-empty catalog-state-inline"
            aria-live="polite"
          >
            No hay materiales que coincidan con la búsqueda actual.
          </div>
        )}

        {!error && rows.length > 0 && (
          <div className="table-responsive catalog-table-scroll orma-catalog-table-wrap">
            <table className="table table-compact mb-0 orma-catalog-table">
              <thead>
                <tr>
                  {renderSortableHeader("material_id", "ID material")}
                  {renderSortableHeader("master_description", "Descripción")}
                  <th>Ubicación física</th>
                  {renderSortableHeader("quantity", "Cantidad", "text-end")}
                  {renderSortableHeader("cost", "Coste", "text-end")}
                  <th className="text-center">MAESTRO</th>
                  <th className="text-center">Seleccionar</th>
                  <th className="text-end">Detalle</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const rowKey = getOrmaRowKey(row);
                  const materialId = String(row.material_id || "").trim();
                  const isSelected = selectedPartIds.has(materialId);
                  const isReturnTarget = selectedRowKey === rowKey;

                  return (
                    <tr
                      key={rowKey}
                      ref={(element) => {
                        if (element) {
                          rowElementsRef.current.set(rowKey, element);
                        } else {
                          rowElementsRef.current.delete(rowKey);
                        }
                      }}
                      className={`catalog-row-link ${
                        isReturnTarget ? "orma-row-return-target" : ""
                      }`}
                      onClick={(event) =>
                        loadDetail(
                          row.material_id,
                          event.currentTarget,
                          rowKey,
                        )
                      }
                      tabIndex={0}
                      aria-label={`Abrir detalle del material ${
                        row.material_id || "sin ID"
                      }`}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          loadDetail(
                            row.material_id,
                            event.currentTarget,
                            rowKey,
                          );
                        }
                      }}
                    >
                      <td className="cell-strong orma-material-cell">
                        {row.material_id || "-"}
                      </td>
                      <td
                        className="orma-description-cell"
                        title={getDescription(row)}
                      >
                        <strong>{getDescription(row)}</strong>
                        <span title={row.brand || ""}>{row.brand || "-"}</span>
                      </td>
                      <td className="orma-location-cell">
                        {renderLocationCell(row)}
                      </td>
                      <td className="text-end">
                        {formatNumber(row.quantity)}
                      </td>
                      <td className="text-end">
                        {formatCurrency(row.cost)}
                      </td>
                      <td className="text-center">
                        <span
                          className={`orma-master-badge ${
                            row.has_master_match
                              ? "orma-master-badge-ok"
                              : "orma-master-badge-missing"
                          }`}
                          title={
                            row.has_master_match
                              ? "Tiene MAESTRO"
                              : "Sin MAESTRO"
                          }
                          aria-label={
                            row.has_master_match
                              ? "Tiene MAESTRO"
                              : "Sin MAESTRO"
                          }
                        >
                          {row.has_master_match ? "Sí" : "No"}
                        </span>
                      </td>
                      <td className="text-center">
                        <button
                          type="button"
                          className={`catalog-detail-button orma-select-part-button ${
                            isSelected ? "orma-select-part-button-active" : ""
                          }`}
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleSelectedPart(row);
                          }}
                          aria-pressed={isSelected}
                        >
                          <i
                            className={
                              isSelected
                                ? "bi bi-bookmark-check-fill"
                                : "bi bi-bookmark-plus"
                            }
                            aria-hidden="true"
                          />
                          {isSelected ? "Guardado" : "Guardar"}
                        </button>
                      </td>
                      <td className="text-end">
                        <button
                          type="button"
                          className="catalog-detail-button"
                          onClick={(event) => {
                            event.stopPropagation();
                            loadDetail(
                              row.material_id,
                              event.currentTarget,
                              rowKey,
                            );
                          }}
                        >
                          Ver
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="catalog-pagination orma-pagination">
          <div className="orma-per-page">
            <label htmlFor="orma_per_page">Resultados</label>
            <select
              id="orma_per_page"
              className="form-select form-select-sm"
              value={perPage}
              onChange={(event) => {
                setPerPage(Number(event.target.value));
                setPage(1);
              }}
              disabled={loading}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
          <button
            className="btn btn-secondary"
            type="button"
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            disabled={loading || page <= 1}
          >
            Anterior
          </button>
          <span className="catalog-pagination-info">
            Página <strong>{formatInteger(totalPages === 0 ? 0 : page)}</strong>{" "}
            de <strong>{formatInteger(totalPages)}</strong>
          </span>
          <button
            className="btn btn-secondary"
            type="button"
            onClick={() =>
              setPage((current) => Math.min(totalPages, current + 1))
            }
            disabled={loading || totalPages === 0 || page >= totalPages}
          >
            Siguiente
          </button>
        </div>
      </section>

      {selectionModalOpen && (
        <div
          className="orma-selection-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeSelectionModal();
            }
          }}
        >
          <section
            className="orma-selection-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="orma-selection-title"
          >
            <div className="orma-selection-header">
              <div>
                <span className="supplier-detail-chip part-detail-chip">
                  Lista temporal
                </span>
                <h2 id="orma-selection-title">Repuestos seleccionados</h2>
                <p>
                  Esta lista se conserva en este navegador para no repetir
                  búsquedas.
                </p>
              </div>
              <button
                ref={selectionCloseButtonRef}
                type="button"
                className="catalog-detail-button"
                onClick={closeSelectionModal}
              >
                Cerrar
              </button>
            </div>

            <div className="orma-selection-body">
              {selectedParts.length === 0 ? (
                <div className="catalog-state catalog-state-empty catalog-state-inline">
                  Todavía no hay repuestos seleccionados.
                </div>
              ) : (
                <div className="table-responsive catalog-table-scroll">
                  <table className="table table-compact mb-0 orma-selection-table">
                    <thead>
                      <tr>
                        <th>ID material</th>
                        <th>Descripción</th>
                        <th>Ubicación seleccionada</th>
                        <th className="text-end">Stock visible</th>
                        <th className="text-end">Cantidad prevista</th>
                        <th className="text-end">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedParts.map((item) => (
                        <tr key={item.material_id}>
                          <td className="cell-strong">
                            {item.material_id}
                          </td>
                          <td>
                            <strong>{item.description || "-"}</strong>
                            {item.brand && <small>{item.brand}</small>}
                          </td>
                          <td>{item.location_summary || "-"}</td>
                          <td className="text-end">
                            {formatNumber(item.available_quantity)}
                          </td>
                          <td className="text-end">
                            <input
                              className="form-control form-control-sm orma-selection-quantity"
                              type="number"
                              min="1"
                              step="1"
                              value={item.planned_quantity}
                              onChange={(event) =>
                                updateSelectedPartQuantity(
                                  item.material_id,
                                  event.target.value,
                                )
                              }
                              aria-label={`Cantidad prevista de ${item.material_id}`}
                            />
                          </td>
                          <td className="text-end">
                            <div className="orma-selection-actions">
                              <button
                                type="button"
                                className="catalog-detail-button"
                                onClick={(event) =>
                                  openSelectedPartDetail(
                                    item,
                                    event.currentTarget,
                                  )
                                }
                              >
                                Ver ficha
                              </button>
                              <button
                                type="button"
                                className="catalog-detail-button orma-selection-remove"
                                onClick={() =>
                                  removeSelectedPart(item.material_id)
                                }
                              >
                                Quitar
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="orma-selection-footer">
              <span>
                {formatInteger(selectedParts.length)} repuesto
                {selectedParts.length === 1 ? "" : "s"} guardado
                {selectedParts.length === 1 ? "" : "s"}
              </span>
              <div>
                <button
                  type="button"
                  className="btn btn-primary orma-export-button"
                  onClick={handleExportSelected}
                  disabled={exportingSelected || selectedParts.length === 0}
                >
                  <i className="bi bi-file-earmark-excel" aria-hidden="true" />
                  {exportingSelected ? "Generando Excel..." : "Exportar Excel"}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={clearSelectedParts}
                  disabled={selectedParts.length === 0}
                >
                  Vaciar lista
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={closeSelectionModal}
                >
                  Seguir buscando
                </button>
              </div>
            </div>
          </section>
        </div>
      )}

      {selectedMaterial && (
        <div
          className="orma-detail-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeDetail();
          }}
        >
          <section
            className="orma-detail-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="orma-detail-title"
          >
            <div className="orma-detail-header">
              <div>
                <span className="supplier-detail-chip part-detail-chip">
                  Material ORMA
                </span>
                <h2 id="orma-detail-title">
                  {material?.material_id || selectedMaterial}
                </h2>
              </div>
              <button
                ref={detailCloseButtonRef}
                type="button"
                className="catalog-detail-button"
                onClick={closeDetail}
                aria-label="Cerrar detalle del material ORMA"
              >
                Cerrar
              </button>
            </div>

            {detailLoading && (
              <div
                className="catalog-state catalog-state-loading catalog-state-inline"
                aria-live="polite"
              >
                Cargando detalle del material...
              </div>
            )}

            {!detailLoading && detailError && (
              <div
                className="catalog-state catalog-state-error catalog-state-inline"
                aria-live="assertive"
              >
                {detailError}
              </div>
            )}

            {!detailLoading &&
              !detailError &&
              hasDetailResponse &&
              !material && (
                <div className="catalog-state catalog-state-empty catalog-state-inline">
                  No hay información disponible para este material.
                </div>
              )}

            {!detailLoading && !detailError && material && (
              <div className="orma-detail-body">
                <section className="orma-detail-section">
                  <h3>Identificación</h3>
                  <div className="orma-detail-grid">
                    <FieldValue
                      label="ID material"
                      value={material.material_id}
                    />
                    <FieldValue
                      label="Descripción VISOR"
                      value={material.visor_description}
                    />
                    <FieldValue
                      label="Descripción MAESTRO"
                      value={material.master_description}
                    />
                    <FieldValue label="Familia" value={material.family} />
                    <FieldValue label="Marca" value={material.brand} />
                    <FieldValue label="Modelo" value={material.model} />
                    <FieldValue
                      label="Ref. fabricante"
                      value={material.manufacturer_ref}
                    />
                    <FieldValue
                      label="Ref. suministrador"
                      value={material.supplier_ref}
                    />
                    <FieldValue label="Alias" value={material.alias} />
                    <FieldValue
                      label="Observaciones"
                      value={material.observations}
                    />
                    <FieldValue
                      label="Tiene MAESTRO"
                      value={material.has_master_match ? "Sí" : "No"}
                    />
                    <FieldValue
                      label="Plano"
                      value={material.drawing_path || "Sin plano asociado"}
                    />
                  </div>
                </section>

                <section className="orma-detail-section">
                  <h3>Resumen</h3>
                  <div className="part-summary-grid orma-summary-grid">
                    <article className="part-summary-item">
                      <span className="part-summary-label">Ubicaciones</span>
                      <strong>{formatInteger(summary.location_count)}</strong>
                    </article>
                    <article className="part-summary-item">
                      <span className="part-summary-label">Almacenes</span>
                      <strong>{formatInteger(summary.warehouse_count)}</strong>
                    </article>
                    <article className="part-summary-item">
                      <span className="part-summary-label">Sucursales</span>
                      <strong>{formatInteger(summary.branch_count)}</strong>
                    </article>
                    <article className="part-summary-item">
                      <span className="part-summary-label">Cantidad total</span>
                      <strong>{formatNumber(summary.total_quantity)}</strong>
                    </article>
                    <article className="part-summary-item">
                      <span className="part-summary-label">Coste total</span>
                      <strong>{formatCurrency(summary.total_cost)}</strong>
                    </article>
                  </div>
                </section>

                {hasInconsistencies && (
                  <section className="orma-inconsistencies">
                    <strong>Inconsistencias detectadas</strong>
                    {Object.entries(inconsistencies).map(([key, values]) =>
                      Array.isArray(values) && values.length > 0 ? (
                        <p key={key}>
                          {key}: {values.join(", ")}
                        </p>
                      ) : null,
                    )}
                  </section>
                )}

                <section className="orma-detail-section">
                  <h3>Ubicaciones y stock</h3>
                  {locations.length === 0 ? (
                    <div className="catalog-state catalog-state-empty catalog-state-inline">
                      No hay ubicaciones registradas para este material.
                    </div>
                  ) : (
                    <div className="table-responsive catalog-table-scroll">
                      <table className="table table-compact mb-0 orma-locations-table">
                        <thead>
                          <tr>
                            <th>Sucursal</th>
                            <th>Almacén</th>
                            <th>Columna</th>
                            <th>Estante</th>
                            <th>Gabeta</th>
                            <th>Segunda ubicación</th>
                            <th className="text-end">Cantidad</th>
                            <th className="text-end">Valor medio</th>
                            <th className="text-end">Coste</th>
                            <th className="text-end">Mínimo</th>
                            <th className="text-end">Máximo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {locations.map((location) => (
                            <tr
                              key={
                                location.id ||
                                `${location.branch_name}-${location.warehouse_id}-${location.column_location}`
                              }
                            >
                              <td>{location.branch_name || "-"}</td>
                              <td>{location.warehouse_id || "-"}</td>
                              <td>{location.column_location || "-"}</td>
                              <td>{location.shelf || "-"}</td>
                              <td>{location.bin || "-"}</td>
                              <td>{location.second_location || "-"}</td>
                              <td className="text-end">
                                {formatNumber(location.quantity)}
                              </td>
                              <td className="text-end">
                                {formatCurrency(location.avg_value)}
                              </td>
                              <td className="text-end">
                                {formatCurrency(location.cost)}
                              </td>
                              <td className="text-end">
                                {formatNumber(location.min_level)}
                              </td>
                              <td className="text-end">
                                {formatNumber(location.max_level)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
};

export default OrmaCatalog;
